import { activePage, replaceActivePage } from "./pages";
import { remapCopiedGroups } from "./groups";
import type { EngineDocument, Point } from "../scene/model";
import { assertNativeDocument } from "./document";
import { bounds, pointBounds, selectable } from "./geometry";

export const clipboardType = "application/x-creator-engine-pro-objects";
export const clipboardMarker = "Объекты Creator Engine Pro";
export function captureClipboard(
  document: EngineDocument,
  ids: readonly string[],
) {
  const selected = new Set(ids);
  const nodes = activePage(document).nodes.filter(
    (n) => selected.has(n.id) && selectable(n),
  );
  if (!nodes.length) return null;
  const needed = new Set(
    nodes.flatMap((n) => Object.values(n.media)).filter(Boolean),
  );
  const snapshot = structuredClone({
    ...document,
    pages: document.pages.map((p) => ({
      ...p,
      nodes: p.id === document.activePageId ? nodes : [],
    })),
    assets: document.assets.filter((a) => needed.has(a.id)),
  });
  assertNativeDocument(snapshot);
  return JSON.stringify({
    format: "creator-engine-pro-objects",
    version: 1,
    document: snapshot,
  });
}
export function readClipboard(text: string): EngineDocument {
  const parsed: unknown = JSON.parse(text);
  if (
    !parsed ||
    typeof parsed !== "object" ||
    !("format" in parsed) ||
    parsed.format !== "creator-engine-pro-objects" ||
    !("version" in parsed) ||
    parsed.version !== 1 ||
    !("document" in parsed)
  )
    throw new Error("Неподдерживаемый буфер объектов.");
  assertNativeDocument(parsed.document);
  if (
    !activePage(parsed.document).nodes.length ||
    activePage(parsed.document).nodes.some((n) => !selectable(n))
  )
    throw new Error("В буфере нет доступных объектов.");
  return parsed.document;
}
/** Atomic scene proposal. Shared immutable assets are reused; conflicting IDs are remapped. */
export function pasteClipboard(
  target: EngineDocument,
  source: EngineDocument,
  center: Point | null,
  newId: () => string,
) {
  assertNativeDocument(source);
  const used = new Set([
    ...target.pages.flatMap((p) => p.nodes.map((n) => n.id)),
    ...target.assets.map((a) => a.id),
  ]);
  const id = () => {
    const value = newId();
    if (!value || used.has(value))
      throw new Error("Не удалось создать уникальный номер объекта.");
    used.add(value);
    return value;
  };
  const assets = target.assets.slice(),
    mapping = new Map<string, string>();
  for (const asset of source.assets) {
    let existing = assets.find(
      (a) => a.kind === asset.kind && a.source === asset.source,
    );
    if (!existing) {
      existing = { ...asset, id: id() };
      assets.push(existing);
    }
    mapping.set(asset.id, existing.id);
  }
  const box = pointBounds(
    activePage(source).nodes.flatMap((n) => {
      const b = bounds(n);
      return [
        { x: b.x, y: b.y },
        { x: b.x + b.width, y: b.y + b.height },
      ];
    }),
  );
  const dx = center ? center.x - box.x - box.width / 2 : 0,
    dy = center ? center.y - box.y - box.height / 2 : 0;
  const copies = activePage(source).nodes.map((n) => {
    const copy = structuredClone(n);
    copy.id = id();
    copy.transform.x = n.transform.x! + dx;
    copy.transform.y = n.transform.y! + dy;
    for (const key of Object.keys(copy.media) as (keyof typeof copy.media)[])
      if (copy.media[key]) copy.media[key] = mapping.get(copy.media[key]!)!;
    if (copy.interaction.objectId) copy.interaction.objectId = copy.id;
    if (
      copy.interaction.targetScreenId &&
      (source.extensions.projectId !== target.extensions.projectId ||
        !target.pages.some((p) => p.id === copy.interaction.targetScreenId))
    )
      copy.interaction.targetScreenId = "";
    return copy;
  });
  remapCopiedGroups(
    copies,
    activePage(target).nodes,
    activePage(source).nodes.map((n) => n.id),
    id,
    !activePage(source).nodes.some((n) =>
      activePage(target).nodes.some((t) => t.id === n.id),
    ),
  );
  const document = replaceActivePage(
    { ...target, assets },
    { ...activePage(target), nodes: [...activePage(target).nodes, ...copies] },
  );
  assertNativeDocument(document);
  return { document, ids: copies.map((n) => n.id) };
}
